import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
    LayoutDashboard,
    Inbox,
    Users,
    Database,
    Banknote,
    Bitcoin,
    Send,
    Settings,
    LogOut,
    Menu,
    X,
    LayoutGrid,
    Megaphone,
    LineChart,
    ArrowDownToLine,
    PenLine,
} from "lucide-react";
import { useState } from "react";
import SkalaLogo from "./SkalaLogo";
import { Button } from "./ui/button";

const navItems = [
    { key: "overview", to: "/admin", Icon: LayoutDashboard, end: true },
    { key: "deposits", to: "/admin/deposits", Icon: ArrowDownToLine },
    { key: "blog", to: "/admin/blog", Icon: PenLine },
    { key: "applications", to: "/admin/applications", Icon: Inbox },
    { key: "users", to: "/admin/users", Icon: Users },
    { key: "ad_accounts", to: "/admin/ad-accounts", Icon: Database },
    { key: "transactions", to: "/admin/transactions", Icon: Banknote },
    { key: "wallets", to: "/admin/wallets", Icon: Bitcoin },
    { key: "sweep", to: "/admin/sweep", Icon: Send },
    { key: "ads_tracking", to: "/admin/ads-tracking", Icon: Megaphone },
    { key: "event_logs", to: "/admin/event-logs", Icon: LineChart },
    { key: "site_settings", to: "/admin/site-settings", Icon: Settings },
];

const labels = {
    overview: "Genel Bakış",
    deposits: "Gelen Ödemeler",
    blog: "Blog Otomasyonu",
    applications: "Başvurular",
    users: "Kullanıcılar",
    ad_accounts: "Reklam Hesapları",
    transactions: "İşlemler",
    wallets: "Cüzdan Adresleri",
    sweep: "Çekim Merkezi",
    ads_tracking: "Ads Tracking Center",
    event_logs: "Realtime Event Log",
    site_settings: "Site İçeriği",
    backPanel: "Kullanıcı paneline dön",
    admin: "Admin",
    defaultName: "Yönetici",
    signOut: "Çıkış yap",
};

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const c = labels;

    return (
        <div
            data-testid="admin-layout"
            className="min-h-screen bg-background text-foreground flex"
        >
            <aside
                className={`${
                    mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
                } fixed md:sticky md:top-0 z-40 h-screen w-72 shrink-0 bg-card/95 backdrop-blur-xl border-r border-primary/10 flex flex-col transition-transform duration-300`}
            >
                <div className="p-6 flex items-center justify-between">
                    <Link to="/" data-testid="admin-logo">
                        <SkalaLogo />
                    </Link>
                    <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary border border-primary/30 bg-primary/10 rounded-full px-2 py-0.5">
                        {c.admin}
                    </span>
                </div>

                <nav className="px-4 flex-1 space-y-1 overflow-y-auto">
                    {navItems.map((it) => (
                        <NavLink
                            key={it.key}
                            to={it.to}
                            end={it.end}
                            onClick={() => setMobileOpen(false)}
                            data-testid={`admin-nav-${it.key}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                                    isActive
                                        ? "bg-primary/10 text-primary border border-primary/20"
                                        : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
                                }`
                            }
                        >
                            <it.Icon className="w-4 h-4" />
                            <span>{c[it.key]}</span>
                        </NavLink>
                    ))}

                    <div className="pt-4 mt-4 border-t border-white/5">
                        <Link
                            to="/panel"
                            data-testid="admin-go-panel"
                            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground/80 hover:text-foreground hover:bg-white/[0.04]"
                        >
                            ← {c.backPanel}
                        </Link>
                    </div>
                </nav>

                <div className="p-4 border-t border-white/5 space-y-3">
                    <div className="flex items-center gap-3 px-2">
                        <div className="w-9 h-9 rounded-full bg-primary/15 grid place-items-center text-primary font-display font-bold text-sm">
                            {(user?.full_name || "A").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold leading-tight truncate">
                                {user?.full_name || c.defaultName}
                            </p>
                            <p className="text-[10px] font-mono text-muted-foreground tracking-wide truncate">
                                {user?.email}
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={logout}
                        data-testid="admin-logout"
                        className="w-full border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                    >
                        <LogOut className="w-4 h-4 mr-2" /> {c.signOut}
                    </Button>
                </div>
            </aside>

            <main className="flex-1 min-w-0">
                <div className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-white/[0.06] bg-background/80 backdrop-blur-xl px-4 h-14">
                    <button
                        onClick={() => setMobileOpen(!mobileOpen)}
                        aria-label="Toggle menu"
                        data-testid="admin-mobile-toggle"
                    >
                        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>
                    <SkalaLogo />
                    <div className="w-6" />
                </div>

                <div className="px-6 md:px-10 py-8 md:py-10 max-w-7xl">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
